/**
 * Summary type constants — match the values in the Umbraco `zSummaryTypeToolbar`
 * DataType dropdown (case-insensitive at runtime; stored values are compared via
 * `.toLowerCase()` everywhere they are read).
 *
 * NOTE: Umbraco stores `statusSummary` (camelCase), but all runtime comparisons
 * normalise to lowercase first, so `'statussummary'` is the canonical form here.
 */

// ---------------------------------------------------------------------------
// Total-summary type identifiers
// ---------------------------------------------------------------------------

export const SUMMARY_TYPE_SUM = 'sum';
export const SUMMARY_TYPE_COUNT = 'count';
export const SUMMARY_TYPE_MIN = 'min';
export const SUMMARY_TYPE_MAX = 'max';
export const SUMMARY_TYPE_AVG = 'avg';

/** Custom DevExtreme-style timesheet aggregation (total hours across TaskName === "Total" rows). */
export const SUMMARY_TYPE_TIMESHEET = 'custom-timesheet-summary';

/**
 * Approval-status aggregation: picks the minimum StatusId across rows and
 * maps it to a human-readable label ("rejected" | "pending" | "approved").
 * Stored in Umbraco as `statusSummary` but compared lowercase as `statussummary`.
 */
export const SUMMARY_TYPE_STATUS = 'statussummary';

/** Sums HH:MM:SS (or decimal-hours) duration fields; formats result as "Sum: HH:MM". */
export const SUMMARY_TYPE_DURATION = 'duration-summary';

/** Sums decimal-hour fields; formats result as "Sum: H:MM". */
export const SUMMARY_TYPE_HOURS_DECIMAL = 'hours-decimal-summary';

/**
 * Full-footer widget — replaces the entire left footer cell with a
 * "Tracked / Manual / Overtime" breakdown sourced from the first loaded row.
 * NOT a per-row aggregation.
 */
export const SUMMARY_TYPE_CUSTOM_LEFT_FOOTER = 'custom-left-footer';

/**
 * Full-footer widget — replaces the entire right footer cell with a
 * currency-amount slider sourced from the first loaded row's `AmountStats` JSON.
 * NOT a per-row aggregation.
 */
export const SUMMARY_TYPE_CUSTOM_RIGHT_FOOTER = 'custom-right-footer';

// ---------------------------------------------------------------------------
// Sets used for O(1) membership checks
// ---------------------------------------------------------------------------

/**
 * All `totalSummaries` values that `DataGridFooter` knows how to render.
 * Ported 1:1 from legacy `grid.js` `calculateCustomSummary` /
 * `onCellPrepared` handlers and the `zSummaryTypeToolbar` Umbraco dropdown.
 *
 * `"custom"` (the plain, un-suffixed BO value) is intentionally excluded —
 * it has no handling in legacy `calculateCustomSummary` either (dead/reserved
 * value in the BO dropdown).
 *
 * `custom-left-footer` and `custom-right-footer` are included so the footer
 * row still renders for grids that have one of these configured;
 * `DataGridFooter` special-cases them into widget cells.
 */
export const SUPPORTED_TOTAL_SUMMARIES = new Set([
  SUMMARY_TYPE_SUM,
  SUMMARY_TYPE_COUNT,
  SUMMARY_TYPE_MIN,
  SUMMARY_TYPE_MAX,
  SUMMARY_TYPE_AVG,
  SUMMARY_TYPE_TIMESHEET,
  SUMMARY_TYPE_STATUS,
  SUMMARY_TYPE_DURATION,
  SUMMARY_TYPE_HOURS_DECIMAL,
  SUMMARY_TYPE_CUSTOM_LEFT_FOOTER,
  SUMMARY_TYPE_CUSTOM_RIGHT_FOOTER,
]);

/** Summary types that are DevExtreme-style standard aggregations (not custom). */
export const STANDARD_SUMMARY_TYPES = new Set([
  SUMMARY_TYPE_SUM,
  SUMMARY_TYPE_COUNT,
  SUMMARY_TYPE_MIN,
  SUMMARY_TYPE_MAX,
  SUMMARY_TYPE_AVG,
]);

/** The only group-summary aggregation currently supported. */
export const SUPPORTED_GROUP_SUMMARIES = new Set([SUMMARY_TYPE_SUM]);

/**
 * Full-footer-replacement types — these render their own React widget instead
 * of a formatted number/text and must be skipped in the text-based `sums` Map.
 */
export const FULL_FOOTER_TYPES = new Set([
  SUMMARY_TYPE_CUSTOM_LEFT_FOOTER,
  SUMMARY_TYPE_CUSTOM_RIGHT_FOOTER,
]);

// ---------------------------------------------------------------------------
// Status mapping (used by status summary aggregation)
// ---------------------------------------------------------------------------

/**
 * Maps a numeric `StatusId` to its display label.
 * Mirrors `statusMapping` in legacy `grid.js` `customStatusSummaryCalculation`.
 * The minimum StatusId across all rows wins and is displayed as its label.
 */
export const STATUS_ID_LABEL_MAP: Record<number, string> = {
  1: 'rejected',
  2: 'pending',
  3: 'approved',
};

// ---------------------------------------------------------------------------
// Currency helpers
// ---------------------------------------------------------------------------

/** Well-known currency code → symbol mapping for `AmountFooterWidget`. */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  UAH: '₴',
};

// ---------------------------------------------------------------------------
// Numeric constants (used by summary calculators)
// ---------------------------------------------------------------------------

export const SECONDS_PER_MINUTE = 60;
export const MINUTES_PER_HOUR = 60;
export const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;

/** Used to round floating-point second totals to 3 decimal places. */
export const SECONDS_ROUNDING_FACTOR = 1000;

/** Minutes in a full hour — used when rounding minutes overflows to 60. */
export const FULL_HOUR_IN_MINUTES = 60;

/** Starting minimum status value — represents the "approved" (highest) status. */
export const STATUS_MIN_START = 3;

/** Number of digits expected after a thousands-separator comma (e.g. "1,234"). */
export const THOUSANDS_GROUP_SIZE = 3;

export const FIXED_DECIMAL_PLACES = 2;
